import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import media from '../../../constants/media';
import theme from '../../../constants/theme';
import { useClientOrServerSideRouting } from '../../../helpers/routing/routeHelpers';
import { IPlanCard } from '../../../interfaces/umbracoNodes/contentSlices';
import RichText from '../../base/richText';
import Button from '../../button-v2';

export const overlayStyle = css`
	position: absolute;
	top: 0;
	left: 0;
	z-index: 1;
	width: 100%;
	height: 100%;
	border-radius: 16px;
	box-shadow: 0 0 30px rgba(0, 0, 0, 0.08);
	transition: box-shadow 0.3s ease-in-out;

	${media.desktopSmall`
		&:hover {
			box-shadow: 0 0 50px rgba(0, 0, 0, 0.15);
		}
	`}
`;

const ClickOverlay = styled.div`
	${overlayStyle}
	cursor: pointer;
`;

const SimpleOverlay = styled.div`
	${overlayStyle}
`;

const TitleWrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
`;

const SubTitle = styled.h4`
	font-size: 32px;
	font-family: ${theme.fonts.SharpSlab};
	font-weight: ${theme.fonts.weight.regular};
	letter-spacing: -0.04em;
	line-height: 1.2;
	color: ${theme.colorsV2.mainRed};
	overflow-wrap: break-word;
`;

const Title = styled.h3`
	font-size: 48px;
	font-family: ${theme.fonts.SharpSlab};
	font-weight: ${theme.fonts.weight.semiBold};
	letter-spacing: -0.04em;
	line-height: 1.2;
	color: ${theme.colorsV2.mainRed};
	max-width: 100%;
	overflow-wrap: break-word;
`;

const SubText = styled.span`
	margin-left: 12px;
	font-size: 14px;
	color: ${theme.colorsV2.mainRed};
	white-space: nowrap;
`;

const RichTextStyled = styled(RichText)`
	margin-top: 32px;
`;

const BottomLine = styled.p`
	margin: 32px 0 0;
	padding: 0;
	color: ${theme.colorsV2.grey6};
	font-size: 16px;
	line-height: 1.3;
`;

const AlignBottomWrapper = styled.div`
	margin-top: auto;
`;

const ButtonStyled = styled(Button)`
	margin-top: 32px;
	width: 100%;
`;

interface CardContainerProps {
	cardMinHeight?: number;
}

const CardContainer = styled.div<CardContainerProps>`
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 56px 24px 24px;
	border-radius: 16px;
	font-family: ${theme.fonts.Arimo};
	background-color: ${theme.colors.white};

	${media.desktopSmall`
        padding: 56px 32px 32px;
		min-height: ${({ cardMinHeight }) => cardMinHeight && `${cardMinHeight}px`};
	`};
`;

interface Props {
	cardMinHeight?: number;
	content: IPlanCard;
	className?: string;
}

const PlanCard: FC<Props> = ({ cardMinHeight, content, className }) => {
	const routeToClientOrServerRoute = useClientOrServerSideRouting();

	const handleClick = (to: string) => {
		routeToClientOrServerRoute(to);
	};

	return (
		<CardContainer className={className} cardMinHeight={cardMinHeight}>
			{content.Values.Link && (
				<ClickOverlay
					role="link"
					data-href={content.Values.Link.Url}
					tabIndex={0}
					onClick={() => handleClick(content.Values.Link.Url)}
				/>
			)}
			{!content.Values.Link && <SimpleOverlay />}
			{content.Values.Subtitle && <SubTitle>{content.Values.Subtitle}</SubTitle>}
			<TitleWrapper>
				{content.Values.Title && <Title>{content.Values.Title}</Title>}
				{content.Values.TitleSubtext && <SubText>{content.Values.TitleSubtext}</SubText>}
			</TitleWrapper>
			<RichTextStyled
				content={content.Values.Description}
				listItemIconUrl={content.Values.DescriptionListItemIcon?.Src}
				isSmall
			/>
			<AlignBottomWrapper>
				{content.Values.GrayText && <BottomLine>{content.Values.GrayText}</BottomLine>}
				{content.Values.Link && content.Values.DisplayButtonForLink && (
					<ButtonStyled title={content.Values.Link.Name} type="secondary" hidden />
				)}
			</AlignBottomWrapper>
		</CardContainer>
	);
};

export default PlanCard;
